// Staff feedback platform — automatic page-context capture.
//
// installFeedbackContextCapture() wraps console.error/console.warn,
// window.onerror/unhandledrejection, and window.fetch so that whenever a
// staff member files a feedback report, the last ~20 console errors/warnings
// and ~20 failed network calls ride along automatically — no "please open
// devtools and copy the console" ask. Ring buffers only; never bodies, never
// auth headers (method + url + status only).
//
// Called once from cos-app-shell's connectedCallback. Idempotent — a second
// call is a no-op so route re-mounts / hot paths can't double-wrap fetch.

import { legacyClientId } from "./legacy-client.js";
import { PARTNER_MODULE_LABELS, type PartnerProduct } from "../partner-admin/catalog.js";
import type { FeedbackConsoleError, FeedbackContext, FeedbackNetworkError, FeedbackTarget } from "../types/feedback.js";

const MAX_ENTRIES = 20;

let installed = false;
const consoleErrors: FeedbackConsoleError[] = [];
const networkErrors: FeedbackNetworkError[] = [];

function pushRing<T>(buf: T[], entry: T): void {
	buf.push(entry);
	if (buf.length > MAX_ENTRIES) buf.shift();
}

function nowIso(): string {
	return new Date().toISOString();
}

function safeString(v: unknown): string {
	try {
		if (v instanceof Error) return v.message;
		if (typeof v === "string") return v;
		return JSON.stringify(v);
	} catch {
		return String(v);
	}
}

/** Core-ops pages that aren't in the partner-admin product catalog. */
const CORE_MODULE_LABELS: Record<string, string> = {
	tickets: "Tickets",
	partners: "Partners",
	projects: "Projects",
	contacts: "Contacts",
	deals: "Deals",
	prospects: "Prospects",
	mail: "Mail",
	chat: "Chat",
	conversations: "Conversations",
	dashboard: "Dashboard",
	dashboards: "Dashboards",
	reports: "Reports",
	inbox: "Inbox",
	notifications: "Notifications",
	settings: "Settings",
	kb: "Knowledge Base",
	files: "Files",
	calendar: "Calendar",
	communications: "Communications",
	activities: "Activities",
	admin: "Admin",
	"partner-admin": "Partner Admin",
	login: "Login",
};

/**
 * Walk the (possibly nested) shadow tree looking for a `data-help-screen`
 * attribute — the same signal cos-circulate-help's `currentScreen()` reads
 * to know which workspace screen is active. Reused here as the `pageKey`
 * for a feedback report on any page that sets it (today: Circulate);
 * absent everywhere else, which is fine — pagePath + module already
 * disambiguate most pages.
 */
export function currentPageKey(): string | undefined {
	const roots: (Element | ShadowRoot)[] = [document.body];
	let i = 0;
	while (i < roots.length) {
		const root = roots[i++];
		for (const node of Array.from(root.querySelectorAll("*"))) {
			const v = node.getAttribute("data-help-screen");
			if (v) return v;
			const sr = (node as HTMLElement).shadowRoot;
			if (sr) roots.push(sr);
		}
	}
	return undefined;
}

/** Derive a human module label from the first path segment. */
export function deriveModule(pathname: string): string | undefined {
	const seg = pathname.split("/").filter(Boolean)[0];
	if (!seg) return "Dashboard";
	const asProduct = PARTNER_MODULE_LABELS[seg as PartnerProduct];
	if (asProduct) return asProduct.main;
	return CORE_MODULE_LABELS[seg] || seg;
}

function onConsoleError(source: "error" | "warn") {
	return (...args: unknown[]) => {
		try {
			pushRing(consoleErrors, {
				message: args.map(safeString).join(" ").slice(0, 2000),
				source,
				at: nowIso(),
			});
		} catch {
			/* never let capture break the app's own logging */
		}
	};
}

function onWindowError(e: ErrorEvent): void {
	try {
		pushRing(consoleErrors, {
			message: `${e.message || "Uncaught error"} (${e.filename ?? ""}:${e.lineno ?? ""})`.slice(0, 2000),
			source: "onerror",
			at: nowIso(),
		});
	} catch {
		/* ignore */
	}
}

function onUnhandledRejection(e: PromiseRejectionEvent): void {
	try {
		pushRing(consoleErrors, {
			message: `Unhandled rejection: ${safeString(e.reason)}`.slice(0, 2000),
			source: "unhandledrejection",
			at: nowIso(),
		});
	} catch {
		/* ignore */
	}
}

function wrapFetch(): void {
	const original = window.fetch.bind(window);
	window.fetch = async (...args: Parameters<typeof fetch>) => {
		const [input, init] = args;
		const method = (init?.method || (input instanceof Request ? input.method : "GET") || "GET").toUpperCase();
		const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
		try {
			const resp = await original(...args);
			if (!resp.ok) {
				pushRing(networkErrors, { method, url, status: resp.status, at: nowIso() });
			}
			return resp;
		} catch (err) {
			pushRing(networkErrors, { method, url, status: 0, at: nowIso() });
			throw err;
		}
	};
}

/**
 * Install the global capture hooks. Idempotent — safe to call from
 * connectedCallback on every app-shell mount.
 */
export function installFeedbackContextCapture(): void {
	if (installed) return;
	installed = true;

	const origError = console.error.bind(console);
	const origWarn = console.warn.bind(console);
	const captureError = onConsoleError("error");
	const captureWarn = onConsoleError("warn");
	console.error = (...args: unknown[]) => {
		captureError(...args);
		origError(...args);
	};
	console.warn = (...args: unknown[]) => {
		captureWarn(...args);
		origWarn(...args);
	};

	window.addEventListener("error", onWindowError);
	window.addEventListener("unhandledrejection", onUnhandledRejection);

	wrapFetch();
}

/** Test-only: reset ring buffers + the installed flag between test cases. */
export function _resetFeedbackContextForTest(): void {
	installed = false;
	consoleErrors.length = 0;
	networkErrors.length = 0;
}

export interface BuildContextOptions {
	pageKey?: string;
	target?: FeedbackTarget;
	/** The signed-in staff member's partner scope, if any (cos-app-shell's userPartnerId). */
	partnerId?: number | null;
}

/** Snapshot the current page + captured environment into a FeedbackContext. */
export function buildFeedbackContext(opts: BuildContextOptions = {}): FeedbackContext {
	return {
		pageUrl: window.location.href,
		pagePath: window.location.pathname,
		pageKey: opts.pageKey ?? currentPageKey(),
		module: deriveModule(window.location.pathname),
		viewport: {
			w: window.innerWidth,
			h: window.innerHeight,
			dpr: window.devicePixelRatio || 1,
		},
		userAgent: navigator.userAgent,
		partnerId: opts.partnerId ?? null,
		legacyClientId: legacyClientId() || null,
		consoleErrors: [...consoleErrors],
		networkErrors: [...networkErrors],
		target: opts.target,
		capturedAt: nowIso(),
	};
}
